import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const IMG = "https://images.unsplash.com/photo-1611523794717-4d1f87dabf3b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

function GoogleButton({ next }) {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    sessionStorage.setItem("qfq_next", next);
    const redirectUrl = window.location.origin + "/mon-espace";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" onClick={go} className="h-12 w-full gap-3 rounded-xl text-base font-semibold" data-testid="google-login-button">
      <svg className="h-5 w-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
      Continuer avec Google
    </Button>
  );
}

function EmailForm({ mode, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(`/auth/${mode}`, body);
      onDone(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${mode}-form`}>
      {mode === "register" && <div><Label>Nom</Label><Input value={form.name} onChange={set("name")} required className="mt-1.5 h-11" data-testid="register-name-input" /></div>}
      <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} required className="mt-1.5 h-11" data-testid={`${mode}-email-input`} /></div>
      <div><Label>Mot de passe</Label><Input type="password" value={form.password} onChange={set("password")} required minLength={mode === "register" ? 6 : 1} className="mt-1.5 h-11" data-testid={`${mode}-password-input`} /></div>
      <Button type="submit" disabled={busy} className="h-12 w-full rounded-xl bg-[#1E40AF] text-base hover:bg-[#1D4ED8] hover:text-white" data-testid={`${mode}-submit-button`}>
        {mode === "register" ? "Créer mon compte" : "Se connecter"}
      </Button>
    </form>
  );
}

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/mon-espace";
  useEffect(() => {
    if (user) navigate(next, { replace: true });
  }, [user, next, navigate]);
  const done = (u) => {
    setUser(u);
    toast.success(`Bienvenue ${u.name || ""}`);
  };
  return (
    <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-2 lg:px-8" data-testid="login-page">
      <div className="relative hidden overflow-hidden rounded-2xl lg:block">
        <img src={IMG} alt="Artisan dans son atelier" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 to-transparent" />
        <p className="absolute bottom-8 left-8 right-8 font-display text-3xl font-bold text-white">Notez les pros qui interviennent chez vous. Gérez votre fiche si vous êtes pro.</p>
      </div>
      <div className="mx-auto w-full max-w-md py-6">
        <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Connexion</h1>
        <p className="mt-2 text-slate-600">Particuliers et professionnels, un seul compte.</p>
        <div className="mt-8"><GoogleButton next={next} /></div>
        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-slate-400"><span className="h-px flex-1 bg-slate-200" />ou par email<span className="h-px flex-1 bg-slate-200" /></div>
        <Tabs defaultValue="login">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" data-testid="login-tab">Se connecter</TabsTrigger>
            <TabsTrigger value="register" data-testid="register-tab">Créer un compte</TabsTrigger>
          </TabsList>
          <TabsContent value="login" className="pt-4"><EmailForm mode="login" onDone={done} /></TabsContent>
          <TabsContent value="register" className="pt-4"><EmailForm mode="register" onDone={done} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
